import { type ReactNode, createContext, useContext, useMemo, useReducer } from 'react';

import type { Article } from '@/lib/ai-agent/schemas/article';

export type ParseErrorKind =
  | 'paywall'
  | 'js-heavy'
  | 'pdf'
  | 'network'
  | 'youtube-unavailable'
  | 'unknown';

export interface ParseError {
  kind: ParseErrorKind;
  message: string;
}

export type WorkbenchFallbackReason = 'timeout' | 'parse_error' | 'unknown';

export interface RepurposeWorkbenchState {
  article: Partial<Article> | null;
  isParsing: boolean;
  parseError: ParseError | null;
  activeArticleId: string | null;
  /**
   * Set when `scan_article` returned `{ fallback: true, fallbackReason }` —
   * the workbench surfaces a muted banner so the user knows why the sections
   * are sparse instead of wondering if the article is just short.
   */
  fallbackReason: WorkbenchFallbackReason | null;
}

export type WorkbenchDispatchEvent =
  | { stage: 'fetching' }
  | { stage: 'parsing'; partial: Partial<Article> }
  | { stage: 'ready'; articleId: string; fallbackReason?: WorkbenchFallbackReason | null }
  | { stage: 'error'; error: ParseError }
  | { stage: 'reset' };

interface Ctx extends RepurposeWorkbenchState {
  dispatch: (e: WorkbenchDispatchEvent) => void;
}

export const WorkbenchCtx = createContext<Ctx | null>(null);

function reducer(
  state: RepurposeWorkbenchState,
  e: WorkbenchDispatchEvent,
): RepurposeWorkbenchState {
  switch (e.stage) {
    case 'fetching':
      return {
        article: null,
        isParsing: true,
        parseError: null,
        activeArticleId: null,
        fallbackReason: null,
      };
    case 'parsing':
      return {
        ...state,
        isParsing: true,
        parseError: null,
        article: { ...(state.article ?? {}), ...e.partial },
      };
    case 'ready':
      return {
        ...state,
        isParsing: false,
        activeArticleId: e.articleId,
        fallbackReason: e.fallbackReason ?? null,
      };
    case 'error':
      return { ...state, isParsing: false, parseError: e.error };
    case 'reset':
      return {
        article: null,
        isParsing: false,
        parseError: null,
        activeArticleId: null,
        fallbackReason: null,
      };
    default:
      return state;
  }
}

const INITIAL_STATE: RepurposeWorkbenchState = {
  article: null,
  isParsing: false,
  parseError: null,
  activeArticleId: null,
  fallbackReason: null,
};

export function RepurposeWorkbenchProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const value = useMemo<Ctx>(() => ({ ...state, dispatch }), [state]);
  return <WorkbenchCtx.Provider value={value}>{children}</WorkbenchCtx.Provider>;
}

export function useRepurposeWorkbench(): Ctx {
  const ctx = useContext(WorkbenchCtx);
  if (!ctx) {
    throw new Error('useRepurposeWorkbench must be used inside RepurposeWorkbenchProvider');
  }
  return ctx;
}

export function useRepurposeWorkbenchSafe(): Ctx | null {
  return useContext(WorkbenchCtx);
}
